import { z } from "zod";
import { POSITION_DECIMALS } from "./geometry";

export const IdSchema = z.string().trim().min(1).max(300);
export const NameSchema = z.string().max(500);
export const DescriptionSchema = z.string().max(100_000);
export const HexColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/);

/** Every list holds at most this many entries, unless its schema sets a lower limit. */
export const MAX_LIST_LENGTH = 100_000;

export function listOf<Entry extends z.ZodType>(entry: Entry) {
  return z.array(entry).max(MAX_LIST_LENGTH);
}

function degrees(min: number, max: number) {
  return z
    .number()
    .min(min)
    .max(max)
    .multipleOf(10 ** -POSITION_DECIMALS);
}

/** `[longitude, latitude]`, in degrees, to at most `POSITION_DECIMALS` decimal places. */
export const PositionSchema = z.tuple([degrees(-180, 180), degrees(-90, 90)]);
export type Position = z.infer<typeof PositionSchema>;

export const JsonValueSchema = z.json().meta({ id: "JsonValue" });

/** Open-ended JSON the game master attaches to a record. */
export const PropertiesSchema = z.record(z.string(), JsonValueSchema);
export type Properties = z.infer<typeof PropertiesSchema>;

/** A `null` value deletes the key; omitted keys are untouched. */
export const PropertiesChangesSchema = z.record(z.string(), JsonValueSchema);
export type PropertiesChanges = z.infer<typeof PropertiesChangesSchema>;

/**
 * A published version of an image, video, or basemap asset from the catalog, by its id, so a
 * new version of the asset never changes a world that named this one.
 */
export const AssetVersionIdSchema = z.uuid();
